import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Eye, Trash2, Phone, Mail, Inbox } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { apiErrorMessage } from "@/lib/api";
import { StatCard, formatDate, SEDE_OPTIONS, AREA_OPTIONS } from "@/components/admin/shared";

const STATUS_LABELS = { nuova: "Nuova", in_lavorazione: "In lavorazione", ricontattato: "Ricontattato", archiviata: "Archiviata" };
const STATUS_STYLES = {
  nuova: "border-gold/50 bg-gold/10 text-gold-dark",
  in_lavorazione: "border-blue-200 bg-blue-50 text-blue-700",
  ricontattato: "border-emerald-200 bg-emerald-50 text-emerald-700",
  archiviata: "border-slate-200 bg-slate-100 text-slate-500",
};

const FilterSelect = ({ label, value, onChange, all, options, testid, width }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`mt-1 ${width}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={all.value}>{all.label}</SelectItem>
        {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const ConsultationRow = ({ r, onView, onDelete }) => (
  <TableRow data-testid={`request-row-${r.id}`}>
    <TableCell className="whitespace-nowrap text-xs text-slate-500">{formatDate(r.created_at)}</TableCell>
    <TableCell className="font-medium text-navy">{r.nome}</TableCell>
    <TableCell className="hidden md:table-cell text-xs text-slate-500">
      <div>{r.email}</div>
      <div>{r.telefono}</div>
    </TableCell>
    <TableCell className="hidden lg:table-cell text-xs">{r.sede}</TableCell>
    <TableCell className="hidden lg:table-cell text-xs">{r.area}</TableCell>
    <TableCell>
      <Badge variant="outline" className={STATUS_STYLES[r.status] || ""} data-testid={`status-badge-${r.id}`}>{STATUS_LABELS[r.status] || r.status}</Badge>
    </TableCell>
    <TableCell className="text-right">
      <button onClick={() => onView(r)} className="p-2 text-slate-500 hover:text-gold-dark transition-colors" data-testid={`view-request-${r.id}`} aria-label="Dettagli"><Eye className="h-4 w-4" /></button>
      <button onClick={() => onDelete(r.id)} className="p-2 text-slate-500 hover:text-red-600 transition-colors" data-testid={`delete-request-${r.id}`} aria-label="Elimina"><Trash2 className="h-4 w-4" /></button>
    </TableCell>
  </TableRow>
);

const ConsultationDialog = ({ selected, onClose, onStatus, onDelete }) => (
  <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-xl" data-testid="request-detail-dialog">
      {selected && (
        <>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{selected.nome}</DialogTitle>
            <DialogDescription>{formatDate(selected.created_at)} — {selected.sede}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-navy hover:text-gold-dark"><Mail className="h-4 w-4 text-gold-dark" />{selected.email}</a>
              <a href={`tel:${selected.telefono}`} className="inline-flex items-center gap-2 text-navy hover:text-gold-dark" data-testid="detail-call-link"><Phone className="h-4 w-4 text-gold-dark" />{selected.telefono}</a>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Area di interesse</p>
              <p className="mt-1 text-navy">{selected.area}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Messaggio</p>
              <p className="mt-1 whitespace-pre-wrap leading-relaxed text-slate-600">{selected.messaggio}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-navy/10 pt-4">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Stato</Label>
              <Select value={selected.status} onValueChange={(v) => onStatus(selected.id, v)}>
                <SelectTrigger className="w-48" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => onDelete(selected.id)} data-testid="detail-delete-button" className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /> Elimina</Button>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const ConsultationsPanel = ({ onUnauthorized }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ sede: "tutte", area: "tutte", status: "tutti" });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.sede !== "tutte") params.sede = filters.sede;
      if (filters.area !== "tutte") params.area = filters.area;
      if (filters.status !== "tutti") params.status = filters.status;
      const [list, st] = await Promise.all([api.get("/consultations", { params }), api.get("/consultations/stats")]);
      setRequests(list.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error("Errore di caricamento", { description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [filters, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/consultations/${id}`, { status });
      toast.success("Stato aggiornato", { description: STATUS_LABELS[status] });
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch (err) {
      toast.error("Aggiornamento non riuscito", { description: apiErrorMessage(err) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/consultations/${id}`);
      toast.success("Richiesta eliminata");
      setSelected(null);
      load();
    } catch (err) {
      toast.error("Eliminazione non riuscita", { description: apiErrorMessage(err) });
    }
  };

  const statCards = [
    { label: "Richieste totali", value: stats?.totale ?? 0, testid: "stat-total" },
    { label: "Nuove", value: stats?.nuova ?? 0, testid: "stat-new" },
    { label: "In lavorazione", value: stats?.in_lavorazione ?? 0, testid: "stat-progress" },
    { label: "Ricontattati", value: stats?.ricontattato ?? 0, testid: "stat-done" },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{statCards.map((s) => <StatCard key={s.label} {...s} />)}</div>

      <div className="mt-10 flex flex-wrap items-end gap-4 border border-navy/10 bg-white p-5">
        <FilterSelect label="Sede" value={filters.sede} onChange={(v) => setFilters((f) => ({ ...f, sede: v }))} all={{ value: "tutte", label: "Tutte le sedi" }} options={SEDE_OPTIONS.map((s) => [s, s])} testid="filter-sede" width="w-56" />
        <FilterSelect label="Area" value={filters.area} onChange={(v) => setFilters((f) => ({ ...f, area: v }))} all={{ value: "tutte", label: "Tutte le aree" }} options={AREA_OPTIONS.map((a) => [a, a])} testid="filter-area" width="w-64" />
        <FilterSelect label="Stato" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} all={{ value: "tutti", label: "Tutti gli stati" }} options={Object.entries(STATUS_LABELS)} testid="filter-status" width="w-48" />
        <Button variant="outline" size="sm" onClick={load} data-testid="filter-refresh" className="h-9"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
      </div>

      <div className="mt-6 border border-navy/10 bg-white" data-testid="admin-requests-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">Contatti</TableHead>
              <TableHead className="hidden lg:table-cell">Sede</TableHead>
              <TableHead className="hidden lg:table-cell">Area</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500">Caricamento...</TableCell></TableRow>
            ) : requests.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-500"><Inbox className="mx-auto mb-3 h-8 w-8 text-slate-300" />Nessuna richiesta presente</TableCell></TableRow>
            ) : (
              requests.map((r) => <ConsultationRow key={r.id} r={r} onView={setSelected} onDelete={remove} />)
            )}
          </TableBody>
        </Table>
      </div>

      <ConsultationDialog selected={selected} onClose={() => setSelected(null)} onStatus={changeStatus} onDelete={remove} />
    </>
  );
};
